import { parse, unparse } from 'papaparse';

export interface CsvExportDateTimeValue {
  date: string;
  time: string;
}

/** API filter / CSV datetime timezone: UTC+8 for /api/ext exports, UTC otherwise. */
export type CsvExportApiTimeZone = 'UTC' | 'UTC+8';

export const CSV_EXPORT_MAX_RANGE_DAYS = 31;
export const CSV_EXPORT_INVALID_RANGE_MESSAGE = 'Incorrect date';
export const CSV_EXPORT_FUTURE_TIME_ERROR_MESSAGE = 'The selected time cannot be in the future.';
export const CSV_EXPORT_RANGE_ERROR_MESSAGE = 'The download time range must be less than or equal to 31 days.';

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const HOUR_IN_MS = 60 * 60 * 1000;
const CSV_DATETIME_COLUMN_NAMES = new Set([ 'date time', 'timestamp', 'datetime', 'date/time' ]);
const CSV_DATETIME_VALUE_REGEXP = /^(\d{4})[/-](\d{2})[/-](\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;

const normalizeTime = (value: string) => value.length === 5 ? `${ value }:00` : value;
const padDateTimePart = (value: number) => String(value).padStart(2, '0');

const toCsvExportDateTimeValue = (value: Date): CsvExportDateTimeValue => ({
  date: [
    value.getFullYear(),
    padDateTimePart(value.getMonth() + 1),
    padDateTimePart(value.getDate()),
  ].join('-'),
  time: [
    padDateTimePart(value.getHours()),
    padDateTimePart(value.getMinutes()),
    padDateTimePart(value.getSeconds()),
  ].join(':'),
});

/** Parse picker date/time as a browser-local wall clock into an absolute instant. */
const toLocalDate = ({ date, time }: CsvExportDateTimeValue) => {
  const [ year, month, day ] = date.split('-').map(Number);
  const [ hours, minutes, seconds ] = normalizeTime(time).split(':').map(Number);

  // Date months are 0–11; YYYY-MM-DD months are 1–12.
  return new Date(year, month - 1, day, hours, minutes, seconds);
};

/** Format an instant as `YYYY-MM-DD HH:mm:ss` in a fixed offset (export APIs reject ISO timezone suffixes). */
const formatDateTimeInOffset = (instant: Date, offsetHours: number) => {
  const shifted = new Date(instant.getTime() + offsetHours * HOUR_IN_MS);

  return [
    [
      shifted.getUTCFullYear(),
      padDateTimePart(shifted.getUTCMonth() + 1),
      padDateTimePart(shifted.getUTCDate()),
    ].join('-'),
    [
      padDateTimePart(shifted.getUTCHours()),
      padDateTimePart(shifted.getUTCMinutes()),
      padDateTimePart(shifted.getUTCSeconds()),
    ].join(':'),
  ].join(' ');
};

export const getCsvExportDateTimeValue = (value: Date = new Date()): CsvExportDateTimeValue => {
  return toCsvExportDateTimeValue(value);
};

export const getCsvExportDefaultRange = (value: Date = new Date()) => ({
  from: getCsvExportDateTimeValue(new Date(value.getTime() - DAY_IN_MS)),
  to: getCsvExportDateTimeValue(value),
});

/**
 * Only confirmed `/api/ext` exports use UTC+8.
 * Standard `/api/v2` and `/api/v1` exports use UTC so unknown resources are not shifted by 8 hours.
 */
const CSV_EXPORT_UTC_PLUS_8_RESOURCES = new Set([
  'csv_export_txs',
  'csv_export_token_transfers',
  'csv_export_token_detail_transfers',
]);

export const getCsvExportApiTimeZone = (resource: string): CsvExportApiTimeZone => {
  if (CSV_EXPORT_UTC_PLUS_8_RESOURCES.has(resource)) {
    return 'UTC+8';
  }

  return 'UTC';
};

/** Rewrite datetime columns only when the body looks like CSV, to avoid corrupting JSON/HTML via papaparse. */
export const shouldLocalizeCsvExportResponse = (contentType: string | null, bodyText: string) => {
  const normalizedContentType = (contentType || '').toLowerCase();
  if (
    normalizedContentType.includes('text/csv') ||
    normalizedContentType.includes('application/csv') ||
    normalizedContentType.includes('application/octet-stream') ||
    normalizedContentType.includes('text/plain')
  ) {
    return true;
  }

  const firstLine = bodyText.trimStart().split(/\r?\n/, 1)[0] || '';
  if (!firstLine || firstLine.startsWith('{') || firstLine.startsWith('[')) {
    return false;
  }

  return firstLine
    .split(',')
    .some((cell) => CSV_DATETIME_COLUMN_NAMES.has(cell.trim().toLowerCase()));
};

/**
 * Picker values are local (same as list UI). Serialize to an absolute instant, then to the
 * target API wall clock as `YYYY-MM-DD HH:mm:ss` (`T`/`Z`/offset forms are rejected by /api/ext).
 */
export const serializeCsvExportDateTime = (
  value: CsvExportDateTimeValue,
  apiTimeZone: CsvExportApiTimeZone = 'UTC',
) => {
  const instant = toLocalDate(value);
  const offsetHours = apiTimeZone === 'UTC' ? 0 : 8;

  return formatDateTimeInOffset(instant, offsetHours);
};

const getApiTimeZoneOffsetHours = (apiTimeZone: CsvExportApiTimeZone) => {
  return apiTimeZone === 'UTC' ? 0 : 8;
};

const formatLocalCsvDateTime = (instant: Date) => {
  return [
    [
      instant.getFullYear(),
      padDateTimePart(instant.getMonth() + 1),
      padDateTimePart(instant.getDate()),
    ].join('/'),
    [
      padDateTimePart(instant.getHours()),
      padDateTimePart(instant.getMinutes()),
      padDateTimePart(instant.getSeconds()),
    ].join(':'),
  ].join(' ');
};

/** Convert an API wall-clock value (V2 UTC or EXT UTC+8) to browser-local wall clock, matching the list UI. */
const convertApiWallClockToLocal = (value: string, offsetHours: number) => {
  const match = CSV_DATETIME_VALUE_REGEXP.exec(value.trim());
  if (!match) {
    return;
  }

  const [ , year, month, day, hours, minutes, seconds ] = match;
  const instant = new Date(Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds),
  ) - offsetHours * HOUR_IN_MS);

  if (Number.isNaN(instant.getTime())) {
    return;
  }

  return formatLocalCsvDateTime(instant);
};

/**
 * After download, rewrite Date Time / Timestamp columns from the API timezone to local time
 * so V2 (UTC) CSV content does not appear ~8 hours off from the list UI.
 */
export const localizeCsvExportContent = (csvText: string, apiTimeZone: CsvExportApiTimeZone) => {
  const parsed = parse<Array<string>>(csvText, {
    header: false,
    skipEmptyLines: false,
  });
  const rows = parsed.data;

  if (!rows.length) {
    return csvText;
  }

  const header = rows[0] ?? [];
  const datetimeColumnIndexes = header.reduce<Array<number>>((result, cell, index) => {
    if (CSV_DATETIME_COLUMN_NAMES.has(String(cell ?? '').trim().toLowerCase())) {
      result.push(index);
    }
    return result;
  }, []);

  if (!datetimeColumnIndexes.length) {
    return csvText;
  }

  const offsetHours = getApiTimeZoneOffsetHours(apiTimeZone);

  for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    if (!row) {
      continue;
    }

    datetimeColumnIndexes.forEach((columnIndex) => {
      const cell = row[columnIndex];
      if (!cell) {
        return;
      }

      const localized = convertApiWallClockToLocal(cell, offsetHours);
      if (localized) {
        row[columnIndex] = localized;
      }
    });
  }

  return unparse(rows, {
    newline: csvText.includes('\r\n') ? '\r\n' : '\n',
  });
};

const getCsvExportDateTimeTimestamp = (value: CsvExportDateTimeValue) => {
  return toLocalDate(value).getTime();
};

export const getCsvExportRangeError = (
  from: CsvExportDateTimeValue,
  to: CsvExportDateTimeValue,
  maxRangeDays: number = CSV_EXPORT_MAX_RANGE_DAYS,
  now: Date = new Date(),
) => {
  if (!from.date || !from.time || !to.date || !to.time) {
    return;
  }

  const fromTimestamp = getCsvExportDateTimeTimestamp(from);
  const toTimestamp = getCsvExportDateTimeTimestamp(to);
  const nowTimestamp = getCsvExportDateTimeTimestamp(getCsvExportDateTimeValue(now));

  if (Number.isNaN(fromTimestamp) || Number.isNaN(toTimestamp) || Number.isNaN(nowTimestamp)) {
    return;
  }

  if (fromTimestamp > toTimestamp) {
    return CSV_EXPORT_INVALID_RANGE_MESSAGE;
  }

  if (toTimestamp > nowTimestamp) {
    return CSV_EXPORT_FUTURE_TIME_ERROR_MESSAGE;
  }

  if (toTimestamp - fromTimestamp > maxRangeDays * DAY_IN_MS) {
    return CSV_EXPORT_RANGE_ERROR_MESSAGE;
  }
};
